import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Icon, Table, Spin, Tabs, notification } from "antd";
import moment from "moment";
import { DrawerDetailsContainer } from "../form/styles";
import TagStatus from "../../../components/utils/tagStatus";
import CustomLink from "../../../components/utils/customLink";
import { numberMask } from "../../../services/helpersMethodsService";
import { callAnimalIndex } from "../../../services/animalService";
import {
  getContractAnimalSearchString,
  getAnimalSortField,
} from "../selectAnimals/utils/getAnimalSearchString";
import {
  getColumnSearchProps,
  getColumnDateSearchProps,
  getColumnNumberSearchProps,
  getColumnDateWithBetweenSearchProps,
} from "../../../utils/tableUtils";

const dateFormat = "DD/MM/YYYY";

const renderFarmStatus = (farmStatus, translation) => {
  const status = translation?.status || {};
  const config = {
    A: {
      text: status.active,
      background: "#C5F1CA",
      borderColor: "#106518",
      color: "#106518",
    },
    S: {
      text: status.sold,
      background: "#FEFFF6",
      borderColor: "#A9C133",
      color: "#A9C133",
    },
    P: {
      text: status.pending,
      background: "#FFDBBC",
      borderColor: "#FE8D2A",
      color: "#FE8D2A",
    },
    D: {
      text: status.dead,
      background: "#4b4b4b",
      borderColor: "#000",
      color: "#e3e3e3",
    },
    T: {
      text: status.transfer,
      background: "#c3c3c3",
      borderColor: "#000",
      color: "#000",
    },
  };
  const inactive = {
    text: status.inactive,
    background: "#FBC7C7",
    borderColor: "#D44C4C",
    color: "#D44C4C",
  };
  const c = config[farmStatus] || inactive;
  return (
    <TagStatus
      background={c.background}
      borderColor={c.borderColor}
      color={c.color}
    >
      {c.text}
    </TagStatus>
  );
};

const ContractDetailsDrawer = ({
  visible,
  contract,
  loading,
  onClose,
  translation,
}) => {
  const t = translation?.boitelContractsPage || {};
  const col = t.table?.column || {};
  const herdsT = t.form?.herds || {};
  const detailsT = t.detailsDrawer || {};
  const animalTranslation = translation?.animal?.columns || {};
  const statusOpt = col.statusOptions || {};
  const genderOpt = col.animalGenderOptions || {};
  const typeOpt = col.contractTypeOptions || {};
  const yesNo = translation?.supplier
    ? { yes: translation.buttons.yes, no: translation.buttons.no }
    : { yes: "Sim", no: "Não" };
  const herds = contract?.herds || [];

  const getStatusConfig = (status) => {
    const config = {
      VIGENTE: {
        text: statusOpt.vigente,
        background: "#C5F1CA",
        borderColor: "#106518",
        color: "#106518",
      },
      ENCERRADO: {
        text: statusOpt.encerrado,
        background: "#E6F7FF",
        borderColor: "#1890FF",
        color: "#1890FF",
      },
      PENDENTE: {
        text: statusOpt.pendente,
        background: "#FFF7E6",
        borderColor: "#FA8C16",
        color: "#FA8C16",
      },
      ERRO: {
        text: statusOpt.erro,
        background: "#FBC7C7",
        borderColor: "#D44C4C",
        color: "#D44C4C",
      },
    };
    return config[status] || config.VIGENTE;
  };

  const getGenderLabel = (gender) =>
    ({
      V: genderOpt.vaca,
      N: genderOpt.novilha,
      I: genderOpt.machoInteiro,
      C: genderOpt.machoCastrado,
    })[gender] || gender;

  const getTypeLabel = (type) =>
    ({
      daily: typeOpt.daily,
      dryMatterConsume: typeOpt.dryMatterConsume,
      arrobaProduction: typeOpt.arrobaProduction,
      partnership: typeOpt.partnership,
    })[type] || type;

  const DetailRow = ({ label, value, span = 8 }) => (
    <Col span={span} style={{ marginBottom: 16 }}>
      <Row>
        <Col span={24}>
          <label>{label}</label>
        </Col>
        <Col span={24}>
          <span className="value">{value ?? "-"}</span>
        </Col>
      </Row>
    </Col>
  );

  const statusConfig = getStatusConfig(contract?.status);

  return (
    <DrawerDetailsContainer
      title={detailsT.title}
      width={1080}
      visible={visible}
      onClose={onClose}
      maskClosable
    >
      <div className="drawerForm">
        <Tabs defaultActiveKey="general" type="card">
          <Tabs.TabPane key="general" tab={detailsT.tabs?.general}>
            <Spin spinning={!!loading}>
              {!contract ? null : (
                <>
                  <Row type="flex" gutter={16}>
                    <DetailRow
                      label={col.identificationContract}
                      value={contract.identificationContract}
                    />
                    <DetailRow
                      label={col.customerName}
                      value={contract.cattleProducerName}
                    />
                    <DetailRow
                      label={col.contractType}
                      value={getTypeLabel(contract.contractType)}
                    />
                    <DetailRow
                      label={col.dateContract}
                      value={
                        contract.contractDate
                          ? moment(contract.contractDate).format(dateFormat)
                          : null
                      }
                    />
                    <DetailRow
                      label={col.finalDateContract}
                      value={
                        contract.finalDateContract
                          ? moment(contract.finalDateContract).format(
                              dateFormat,
                            )
                          : null
                      }
                    />
                    <Col span={8} style={{ marginBottom: 16 }}>
                      <Row>
                        <Col span={24}>
                          <label>{col.status}</label>
                        </Col>
                        <Col span={24}>
                          <TagStatus
                            background={statusConfig.background}
                            borderColor={statusConfig.borderColor}
                            color={statusConfig.color}
                          >
                            {statusConfig.text}
                          </TagStatus>
                        </Col>
                      </Row>
                    </Col>
                    <DetailRow
                      label={col.amountAnimals}
                      value={contract.amountAnimals}
                    />
                    <DetailRow
                      label={col.selectedAnimals}
                      value={contract.selectedAnimals}
                    />
                    <DetailRow
                      label={col.animalGender}
                      value={getGenderLabel(contract.animalGender)}
                    />
                    <DetailRow
                      label={col.entryWeight}
                      value={
                        contract.entryWeight != null
                          ? numberMask(contract.entryWeight, false)
                          : null
                      }
                    />
                    <DetailRow
                      label={col.contractDays}
                      value={contract.contractDays}
                    />
                    <DetailRow
                      label={col.consecutiveDays}
                      value={contract.consecutiveDays}
                    />
                    <Col span={8} style={{ marginBottom: 16 }}>
                      <Row>
                        <Col span={24}>
                          <label>{col.fileUrl}</label>
                        </Col>
                        <Col span={24}>
                          {contract.fileUrl ? (
                            <a
                              href={contract.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon type="file" />
                            </a>
                          ) : (
                            "-"
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {herds.length > 0 && (
                    <>
                      <Row
                        type="flex"
                        style={{ marginTop: 24, marginBottom: 8 }}
                      >
                        <Col span={24}>
                          <label className="sectionTitle">
                            {t.form?.sections?.herd}
                          </label>
                        </Col>
                      </Row>
                      <Table
                        rowKey="id"
                        dataSource={herds}
                        pagination={false}
                        size="small"
                        scroll={{ x: true }}
                        style={{ marginBottom: 16 }}
                      >
                        <Table.Column
                          dataIndex="cjt"
                          title={herdsT.cjt}
                          align="center"
                          width={60}
                          render={(_, __, index) => index + 1}
                        />
                        <Table.Column
                          dataIndex="amountAnimals"
                          title={herdsT.amountAnimals}
                          align="center"
                        />
                        <Table.Column
                          dataIndex="breedName"
                          title={herdsT.breed}
                          align="left"
                          render={(v) => v || "-"}
                        />
                        <Table.Column
                          dataIndex="weightAverage"
                          title={herdsT.weightAverage}
                          align="center"
                          render={(v) =>
                            v != null ? Number(v).toFixed(2) : "-"
                          }
                        />
                        <Table.Column
                          dataIndex="useSisbov"
                          title={herdsT.useSisbov}
                          align="center"
                          render={(v) => (v ? yesNo.yes : yesNo.no)}
                        />
                        <Table.Column
                          dataIndex="trace"
                          title={herdsT.trace}
                          align="center"
                          render={(v) => (v ? yesNo.yes : yesNo.no)}
                        />
                        <Table.Column
                          dataIndex="entryDate"
                          title={herdsT.entryDate}
                          align="center"
                          render={(date) =>
                            date ? moment(date).format(dateFormat) : "-"
                          }
                        />
                        <Table.Column
                          dataIndex="supplementName"
                          title={herdsT.supplement}
                          align="left"
                          render={(v) => v || "-"}
                        />
                        <Table.Column
                          dataIndex="farmName"
                          title={herdsT.farm}
                          align="left"
                          render={(v) => v || "-"}
                        />
                      </Table>
                    </>
                  )}
                </>
              )}
            </Spin>
          </Tabs.TabPane>
          <Tabs.TabPane key="animals" tab={detailsT.tabs?.animals}>
            <ContractAnimalsTab
              visible={visible}
              contractId={contract?.id}
              translation={translation}
              animalTranslation={animalTranslation}
              detailsT={detailsT}
            />
          </Tabs.TabPane>
        </Tabs>
      </div>
    </DrawerDetailsContainer>
  );
};

const ContractAnimalsTab = ({
  visible,
  contractId,
  translation,
  animalTranslation,
  detailsT,
}) => {
  const {
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const [loadingAnimals, setLoadingAnimals] = useState(false);
  const [animalData, setAnimalData] = useState({
    content: [],
    totalElements: 0,
    number: 0,
    size: 10,
  });
  const [tableState, setTableState] = useState({
    page: 1,
    size: 10,
    sorter: null,
    filters: {},
  });

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const getColumnTextSearchProps = useCallback(
    (dataIndex) => getColumnSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const getColumnDateFilterProps = useCallback(
    (dataIndex, useBetween = false) =>
      useBetween
        ? getColumnDateWithBetweenSearchProps(
            dataIndex,
            handleReset,
            handleSearch,
          )
        : getColumnDateSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const getColumnNumberFilterProps = useCallback(
    (dataIndex) =>
      getColumnNumberSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const fetchAnimals = useCallback(
    async (page, size, sorter, filters) => {
      if (!contractId) return;
      setLoadingAnimals(true);
      try {
        const search = getContractAnimalSearchString(contractId, filters);
        const sortField = getAnimalSortField(sorter);
        const sorterPayload =
          sortField && sorter?.order
            ? { field: sortField, order: sorter.order }
            : null;

        const {
          data: { results },
        } = await callAnimalIndex({
          groupId,
          farmId,
          page: page || 1,
          size: size || 10,
          sorter: sorterPayload,
          filters: search,
        });

        const content = results?.content ?? results ?? [];
        const total = results?.totalElements ?? content.length;
        const num = results?.number ?? page - 1;
        const sz = results?.size ?? size;

        setAnimalData({
          content,
          totalElements: total,
          number: num,
          size: sz,
        });
      } catch (err) {
        notification.error({
          message: translation?.error?.error || "Erro",
          description:
            detailsT.errorLoadingAnimals || "Erro ao carregar animais.",
        });
        setAnimalData((prev) => ({ ...prev, content: [] }));
      } finally {
        setLoadingAnimals(false);
      }
    },
    [contractId, groupId, farmId, translation, detailsT.errorLoadingAnimals],
  );

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setTableState({
      page: pagination.current,
      size: pagination.pageSize,
      sorter: sorter?.columnKey ? sorter : null,
      filters: filters || {},
    });
  }, []);

  // Reset table state when the drawer closes or the contract changes
  useEffect(() => {
    setTableState({ page: 1, size: 10, sorter: null, filters: {} });
    setAnimalData({ content: [], totalElements: 0, number: 0, size: 10 });
  }, [contractId]);

  useEffect(() => {
    if (visible && contractId) {
      fetchAnimals(
        tableState.page,
        tableState.size,
        tableState.sorter,
        tableState.filters,
      );
    }
  }, [
    visible,
    contractId,
    tableState.page,
    tableState.size,
    tableState.sorter,
    tableState.filters,
    fetchAnimals,
  ]);

  return (
    <Table
      rowKey="id"
      loading={loadingAnimals}
      dataSource={animalData.content}
      pagination={{
        total: animalData.totalElements,
        current: tableState.page,
        pageSize: tableState.size,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100", "200"],
        hideOnSinglePage: animalData.totalElements <= 10,
      }}
      onChange={handleTableChange}
      size="small"
      scroll={{ x: true }}
      locale={{
        emptyText: detailsT.emptyAnimals || "Nenhum animal selecionado.",
      }}
    >
      <Table.Column
        title={animalTranslation.handlingNumber || "Identificação"}
        dataIndex="handlingNumber"
        key="handlingNumber"
        align="left"
        width={160}
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "handlingNumber"
            ? tableState.sorter.order
            : null
        }
        {...getColumnTextSearchProps("handlingNumber")}
        render={(text, record) => {
          const label =
            record.handlingNumber ||
            record.tagId ||
            record.sisbov ||
            record.nickname ||
            "-";
          return record.id ? (
            <CustomLink to={`/admin/animals/${record.id}`}>{label}</CustomLink>
          ) : (
            label
          );
        }}
      />
      <Table.Column
        title={animalTranslation.tagId || "Id. Eletrônico"}
        dataIndex="tagId"
        key="tagId"
        width={140}
        align="left"
        {...getColumnTextSearchProps("tagId")}
      />
      <Table.Column
        title={animalTranslation.sisbov || "Sisbov"}
        dataIndex="sisbov"
        key="sisbov"
        width={140}
        align="left"
        {...getColumnTextSearchProps("sisbov")}
        render={(text) => (text && text > 0 ? text : "")}
      />
      <Table.Column
        title={translation?.animal?.searchAnimal?.nickname || "Nome"}
        dataIndex="nickname"
        key="nickname"
        width={140}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "nickname"
            ? tableState.sorter.order
            : null
        }
        {...getColumnTextSearchProps("nickname")}
        render={(text) => text || ""}
      />
      <Table.Column
        title={translation?.animal?.searchAnimal?.registry || "Registro"}
        dataIndex="registry"
        key="registry"
        width={140}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "registry"
            ? tableState.sorter.order
            : null
        }
        {...getColumnTextSearchProps("registry")}
        render={(text) => text || ""}
      />
      <Table.Column
        title={animalTranslation.status || "Status"}
        dataIndex="farmStatus"
        key="farmStatus"
        width={120}
        align="left"
        filteredValue={tableState.filters?.farmStatus || null}
        filters={[
          { text: translation?.status?.active || "Ativo", value: "A" },
          { text: translation?.status?.inactive || "Inativo", value: "I" },
          { text: translation?.status?.sold || "Vendido", value: "S" },
          { text: translation?.status?.pending || "Pendente", value: "P" },
          { text: translation?.status?.dead || "Morto", value: "D" },
          {
            text: translation?.status?.transfer || "Transferência",
            value: "T",
          },
        ]}
        filterMultiple
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "farmStatus"
            ? tableState.sorter.order
            : null
        }
        render={(farmStatus) => renderFarmStatus(farmStatus, translation)}
      />
      <Table.Column
        title={animalTranslation.picket || "Piquete"}
        dataIndex="picketName"
        key="picketName"
        width={140}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "picketName"
            ? tableState.sorter.order
            : null
        }
        {...getColumnTextSearchProps("picketName")}
      />
      <Table.Column
        title={animalTranslation.lot || "Lote"}
        dataIndex="lotName"
        key="lotName"
        width={140}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "lotName"
            ? tableState.sorter.order
            : null
        }
        {...getColumnTextSearchProps("lotName")}
      />
      <Table.Column
        title={animalTranslation.breed || "Raça"}
        dataIndex="breedName"
        key="breedName"
        width={120}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "breedName"
            ? tableState.sorter.order
            : null
        }
        {...getColumnTextSearchProps("breedName")}
      />
      <Table.Column
        title={animalTranslation.gender || "Sexo"}
        dataIndex="gender"
        key="gender"
        width={100}
        align="left"
        filteredValue={tableState.filters?.gender || null}
        filters={[
          { text: translation?.animal?.details?.male || "Macho", value: "M" },
          { text: translation?.animal?.details?.female || "Fêmea", value: "F" },
        ]}
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "gender"
            ? tableState.sorter.order
            : null
        }
        render={(g) =>
          g === "M"
            ? translation?.animal?.details?.male || "Macho"
            : translation?.animal?.details?.female || "Fêmea"
        }
      />
      <Table.Column
        title={animalTranslation.birthdayMonths || "Idade"}
        dataIndex="birthdayMonth"
        key="birthdayMonth"
        width={80}
        align="left"
        render={(text, record) =>
          record.birthdayMonth != null ? `${record.birthdayMonth}m` : "-"
        }
      />
      <Table.Column
        title={animalTranslation.acquisitionDate || "Data de Entrada"}
        dataIndex="acquisitionDate"
        key="acquisitionDate"
        width={120}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "acquisitionDate"
            ? tableState.sorter.order
            : null
        }
        render={(text) => (text ? moment(text).format(dateFormat) : "-")}
        {...getColumnDateFilterProps("acquisitionDate", true)}
      />
      <Table.Column
        title={animalTranslation.supplierName || "Fornecedor"}
        dataIndex="supplierName"
        key="supplierName"
        width={140}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "supplierName"
            ? tableState.sorter.order
            : null
        }
        render={(text) => text || "-"}
        {...getColumnTextSearchProps("supplierName")}
      />
      <Table.Column
        title={animalTranslation.currentWeight || "Últ. Peso"}
        dataIndex="lastDailyWeight"
        key="lastDailyWeight"
        width={120}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "lastDailyWeight"
            ? tableState.sorter.order
            : null
        }
        {...getColumnNumberFilterProps("lastDailyWeight")}
        render={(text) => (text != null ? `${numberMask(text, false)}kg` : "-")}
      />
      <Table.Column
        title={animalTranslation.gdp || "GMD"}
        dataIndex="lastDailyWeightGain"
        key="lastDailyWeightGain"
        width={120}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "lastDailyWeightGain"
            ? tableState.sorter.order
            : null
        }
        {...getColumnNumberFilterProps("lastDailyWeightGain")}
        render={(text) =>
          text != null ? `${numberMask(text, false)} kg` : "-"
        }
      />
      <Table.Column
        title={animalTranslation.lastDailyWeightDate || "Últ. Pesagem"}
        dataIndex="lastDailyWeightDate"
        key="lastDailyWeightDate"
        width={140}
        align="left"
        sorter
        sortOrder={
          tableState.sorter?.columnKey === "lastDailyWeightDate"
            ? tableState.sorter.order
            : null
        }
        {...getColumnDateFilterProps("lastDailyWeightDate", true)}
        render={(text) => (text ? moment(text).format(dateFormat) : "-")}
      />
    </Table>
  );
};

export default ContractDetailsDrawer;
